import { getPermissionsForRoles, MemberRoleEnum, PermissionsEnum, resolveMemberRole } from '@novu/shared';
import React from 'react';
import { createContextHook } from '../context';
import { DecodedJwt } from '.';
import { getJwtToken, isJwtValid } from './jwt-manager';
import { createUserFromJwt, SelfHostedUser } from './user.types';

export type SelfHostedAuthContextValue = {
  currentUser: SelfHostedUser | null;
  has: (params: { permission: PermissionsEnum } | { role: MemberRoleEnum }) => boolean;
};

const defaultAuthContextValue: SelfHostedAuthContextValue = {
  currentUser: null,
  has: () => true,
};

export const AuthContext = React.createContext<SelfHostedAuthContextValue>(defaultAuthContextValue);

export function AuthContextProvider({ children }: any) {
  const jwt = getJwtToken();
  const decodedJwt: DecodedJwt | null = jwt && isJwtValid(jwt) ? JSON.parse(atob(jwt.split('.')[1])) : null;

  const roleKey = (decodedJwt?.roles ?? []).join(',');

  /**
   * Mirrors the API's `CommunityPermissionsGuard`: the same role table decides what the UI offers
   * and what the server accepts, so a control is never hidden when the request would have worked —
   * nor offered when it would be rejected.
   */
  const has = React.useMemo<SelfHostedAuthContextValue['has']>(() => {
    const roles = roleKey ? (roleKey.split(',') as MemberRoleEnum[]) : [];
    const permissions = getPermissionsForRoles(roles);

    return (params) => {
      if ('permission' in params) {
        return permissions.includes(params.permission);
      }

      if ('role' in params) {
        return roles.some((role) => resolveMemberRole(role) === resolveMemberRole(params.role));
      }

      return false;
    };
  }, [roleKey]);

  const value = {
    currentUser: createUserFromJwt(decodedJwt),
    has,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuthContext = createContextHook(AuthContext);
